/**
 * EvalCaseTreePicker — the run configurator's case picker.
 *
 * Replaces a flat 160px scroll box of `caseName [suite]` checkboxes with a
 * ONE-LEVEL tree grouped by `suite` plus a facet chip bar.
 *
 * Why one level and not a real tree: `suite` is the only single-valued,
 * well-distributed field in the corpus (12 groups of 2–15) and is already the
 * backend's own run-selection unit. `tags` crosscut suites badly (`feedback`
 * and `regression` each appear in 8 different suites, 2/3 of cases carry more
 * than one tag), so nesting them would duplicate most of the corpus and still
 * could not express "every regression case, across all suites". Facets FILTER,
 * the tree GROUPS — together they express what a nested tree structurally
 * cannot. `lang` (86% de) and `confidence` (65% medium) are too skewed to be
 * levels at all.
 *
 * The helpers below are exported and pure so the selection/filter semantics can
 * be tested without a DOM.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { EvalCaseHoverCard } from './EvalCaseHoverCard.jsx';

export const NO_SUITE_KEY = '__no_suite__';
export const NO_SUITE_LABEL = '— no suite —';

/** Search covers the fields that actually distinguish cases — `prompt` above all. */
export function caseMatchesSearch(c, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return (
    (c.caseName || '').toLowerCase().includes(q) ||
    (c.suite || '').toLowerCase().includes(q) ||
    (c.prompt || '').toLowerCase().includes(q) ||
    (c.tags || []).some((t) => (t || '').toLowerCase().includes(q))
  );
}

/**
 * OR within a facet type, AND across types. `activeOnly` is a facet too, and it
 * defaults ON because the backend silently drops inactive cases
 * (`repository.py::resolve_eval_case_names` filters `is_active`) — an inactive
 * case that looks ticked here would just vanish from the run.
 */
export function caseMatchesFacets(c, facets) {
  const { tags = [], langs = [], confidences = [], activeOnly = true } = facets || {};
  if (activeOnly && !c.isActive) return false;
  if (tags.length && !(c.tags || []).some((t) => tags.includes(t))) return false;
  if (langs.length && !langs.includes(c.lang || '')) return false;
  if (confidences.length && !confidences.includes(c.confidence || '')) return false;
  return true;
}

/** Distinct facet values across the whole corpus, each sorted, for the chip bar. */
export function collectFacetValues(cases) {
  const tags = new Set();
  const langs = new Set();
  const confidences = new Set();
  for (const c of cases || []) {
    for (const t of c.tags || []) tags.add(t);
    langs.add(c.lang || '');
    confidences.add(c.confidence || '');
  }
  const sorted = (s) => [...s].sort();
  return { tags: sorted(tags), langs: sorted(langs), confidences: sorted(confidences) };
}

/** Group by suite, alphabetically, with the null-suite bucket always last. */
export function groupCasesBySuite(cases) {
  const groups = new Map();
  for (const c of cases || []) {
    const key = c.suite || NO_SUITE_KEY;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === NO_SUITE_KEY) return 1;
      if (b === NO_SUITE_KEY) return -1;
      return a.localeCompare(b);
    })
    .map(([key, groupCases]) => ({
      key,
      label: key === NO_SUITE_KEY ? NO_SUITE_LABEL : key,
      cases: groupCases,
    }));
}

/**
 * Case NAMES the backend would actually add for a `suite=` run — ACTIVE cases
 * only, mirroring `resolve_eval_case_names`. Used for both the pre-checked rows
 * here and the union count in the configurator, so the two can never disagree.
 *
 * Keyed by `caseName`, not `id`: the corpus is a repo file now and carries no
 * integer id.
 */
export function suiteCaseNameSet(cases, suite) {
  if (!suite) return new Set();
  return new Set((cases || []).filter((c) => c.suite === suite && c.isActive).map((c) => c.caseName));
}

function FacetChip({ label, active, onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      className={`ion-eval-facet-chip${active ? ' ion-eval-facet-chip--on' : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/** A checkbox that can also render the "some but not all" third state. */
function TriStateCheckbox({ checked, indeterminate, disabled, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked;
  }, [indeterminate, checked]);
  return (
    <input type="checkbox" ref={ref} checked={checked} disabled={disabled} onChange={onChange} />
  );
}

export function EvalCaseTreePicker({ cases, selectedNames, onChange, suiteFromDropdown }) {
  const [search, setSearch] = useState('');
  const [tagFacets, setTagFacets] = useState([]);
  const [langFacets, setLangFacets] = useState([]);
  const [confidenceFacets, setConfidenceFacets] = useState([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const facetValues = useMemo(() => collectFacetValues(cases), [cases]);
  const suiteNames = useMemo(
    () => suiteCaseNameSet(cases, suiteFromDropdown),
    [cases, suiteFromDropdown]
  );

  const visibleCases = useMemo(
    () =>
      (cases || []).filter(
        (c) =>
          caseMatchesSearch(c, search) &&
          caseMatchesFacets(c, {
            tags: tagFacets,
            langs: langFacets,
            confidences: confidenceFacets,
            activeOnly,
          })
      ),
    [cases, search, tagFacets, langFacets, confidenceFacets, activeOnly]
  );

  const groups = useMemo(() => groupCasesBySuite(visibleCases), [visibleCases]);
  // Denominator for "of M in suite": the whole suite, pre-facet.
  const totalPerSuite = useMemo(() => {
    const counts = new Map();
    for (const c of cases || []) {
      const key = c.suite || NO_SUITE_KEY;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [cases]);

  const selected = useMemo(() => new Set(selectedNames), [selectedNames]);

  const toggleIn = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const addNames = (names) => onChange([...new Set([...selectedNames, ...names])]);
  const removeNames = (names) => {
    const drop = new Set(names);
    onChange(selectedNames.filter((name) => !drop.has(name)));
  };
  const toggleCase = (name) =>
    selected.has(name) ? removeNames([name]) : addNames([name]);

  // A suite-included case is already in the run; ticking it explicitly is a
  // no-op, so it is checked+disabled and never counted as a group selection.
  const selectableOf = (list) => list.filter((c) => !suiteNames.has(c.caseName)).map((c) => c.caseName);

  const toggleGroup = (groupCases) => {
    const names = selectableOf(groupCases);
    const allOn = names.length > 0 && names.every((name) => selected.has(name));
    if (allOn) removeNames(names);
    else addNames(names);
  };

  const toggleCollapsed = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const facetRow = (label, values, current, setCurrent, emptyLabel) => {
    if (!values.length) return null;
    return (
      <div className="ion-eval-facet-row">
        <span className="ion-eval-facet-label">{label}</span>
        {values.map((v) => (
          <FacetChip
            key={v || '__empty__'}
            label={v || emptyLabel}
            active={current.includes(v)}
            onClick={() => toggleIn(current, setCurrent, v)}
          />
        ))}
      </div>
    );
  };

  const anyFacetOn =
    tagFacets.length > 0 || langFacets.length > 0 || confidenceFacets.length > 0 || !activeOnly;

  return (
    <div className="ion-eval-casetree">
      <input
        type="text"
        placeholder="Search case name, suite, prompt or tag…"
        value={search}
        onInput={(e) => setSearch(e.target.value)}
        className="ion-eval-casetree-search"
      />

      <div className="ion-eval-facets">
        <div className="ion-eval-facet-row">
          <span className="ion-eval-facet-label">Status</span>
          <FacetChip
            label="Active only"
            active={activeOnly}
            title="Inactive cases are dropped by the backend when the run resolves — leave this on unless you are auditing the corpus."
            onClick={() => setActiveOnly((v) => !v)}
          />
        </div>
        {facetRow('Tags', facetValues.tags, tagFacets, setTagFacets, 'untagged')}
        {facetRow('Lang', facetValues.langs, langFacets, setLangFacets, 'no lang')}
        {facetRow(
          'Confidence',
          facetValues.confidences,
          confidenceFacets,
          setConfidenceFacets,
          'unrated'
        )}
        {anyFacetOn && (
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--ghost ion-eval-facet-reset"
            onClick={() => {
              setTagFacets([]);
              setLangFacets([]);
              setConfidenceFacets([]);
              setActiveOnly(true);
            }}
          >
            Reset filters
          </button>
        )}
      </div>

      <div className="ion-eval-casetree-bulk">
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={() => addNames(selectableOf(visibleCases))}
        >
          Select all visible ({visibleCases.length})
        </button>{' '}
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={() => removeNames(visibleCases.map((c) => c.caseName))}
        >
          Clear visible
        </button>
      </div>

      <div className="ion-eval-casetree-box">
        {groups.map((group) => {
          const names = selectableOf(group.cases);
          const nSelected = group.cases.filter(
            (c) => selected.has(c.caseName) || suiteNames.has(c.caseName)
          ).length;
          const allOn = names.length > 0 && names.every((name) => selected.has(name));
          const isCollapsed = collapsed.has(group.key);
          return (
            <div key={group.key} className="ion-eval-casetree-group">
              <div className="ion-eval-casetree-grouphead">
                <TriStateCheckbox
                  checked={allOn}
                  indeterminate={nSelected > 0}
                  disabled={names.length === 0}
                  onChange={() => toggleGroup(group.cases)}
                />
                <button
                  type="button"
                  className="ion-eval-casetree-toggle"
                  onClick={() => toggleCollapsed(group.key)}
                  aria-expanded={!isCollapsed}
                >
                  {isCollapsed ? '▸' : '▾'} {group.label}
                </button>
                <span className="ion-eval-casetree-groupcount">
                  {nSelected} selected / {group.cases.length} visible (of{' '}
                  {totalPerSuite.get(group.key) || group.cases.length} in suite)
                </span>
              </div>
              {!isCollapsed &&
                group.cases.map((c) => {
                  const fromSuite = suiteNames.has(c.caseName) && !selected.has(c.caseName);
                  return (
                    <EvalCaseHoverCard
                      key={c.caseName}
                      case={c}
                      viaSuite={fromSuite ? suiteFromDropdown : null}
                    >
                      <label
                        className={`ion-eval-casetree-row${
                          fromSuite ? ' ion-eval-casetree-row--viasuite' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(c.caseName) || suiteNames.has(c.caseName)}
                          disabled={fromSuite}
                          onChange={() => toggleCase(c.caseName)}
                        />{' '}
                        <span className="ion-eval-casetree-name">{c.caseName}</span>
                        {!c.isActive && (
                          <span className="ion-eval-casetree-tag ion-eval-casetree-tag--warn">
                            inactive
                          </span>
                        )}
                        {fromSuite && <span className="ion-eval-casetree-tag">via suite</span>}
                      </label>
                    </EvalCaseHoverCard>
                  );
                })}
            </div>
          );
        })}
        {visibleCases.length === 0 && (
          <div className="admin-aim-field-hint">No cases match the current search and filters.</div>
        )}
      </div>
    </div>
  );
}
